import React from 'react';
import { CheckIcon, ClockIcon, UsersIcon, StarIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useAuthContext } from '../hooks/useAuthContext';
import { auth } from '../services/firebase';

const WaitlistView = () => {
  const { user, userProfile, profileLoading, authIsReady, dispatch } = useAuthContext();

  // Get user's first name for personalization with fallbacks
  const firstName = userProfile?.basicInfo?.firstName || 
                    userProfile?.displayName?.split(' ')[0] || 
                    user?.displayName?.split(' ')[0] || 
                    'there';

  const handleLogout = async () => {
    try {
      // If user is authenticated, sign them out
      if (user) {
        await auth.signOut();
        dispatch({ type: 'LOGOUT' });
      }
      // Redirect to home page after logout (whether user was authenticated or not)
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, redirect to home page
      window.location.href = '/';
    }
  };

  // Show loading state while auth or profile is loading
  if (!authIsReady || profileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your waitlist status...</p>
        </div>
      </div>
    );
  }

  // If no user, redirect to home (shouldn't happen, but safety check)
  if (!user) {
    window.location.href = '/';
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-2">
              <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-8" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent">burnt</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-500">
                Waitlist Member
              </div>
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Message */}
        <div className="text-center mb-12">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100 mb-6">
            <CheckIcon className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to Burnt, {firstName}! 🎉
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Congratulations! You're now on the waitlist for our exclusive network of top performers. 
            We'll notify you as soon as your spot is ready.
          </p>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
              <ClockIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Your Status</h2>
              <p className="text-gray-600">Waitlist Position</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-700 uppercase tracking-wide">Current Status</p>
                <p className="text-2xl font-bold text-purple-900">Waitlist Member</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-purple-600">Estimated Access</p>
                <p className="text-lg font-semibold text-purple-800">Coming Soon</p>
              </div>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                <UsersIcon className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">What to Expect</h3>
            </div>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Email notification when your spot is ready</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Access to our exclusive member network</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Curated introductions to top performers</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Private events and networking opportunities</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
                <StarIcon className="h-5 w-5 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Stay Connected</h3>
            </div>
            <div className="space-y-4 text-gray-600">
              <p>
                Follow us on LinkedIn for updates and behind-the-scenes content from the Burnt community.
              </p>
              <div className="flex gap-4">
                <a 
                  href="https://www.linkedin.com/company/106855639" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Follow on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm">
          <p>Questions? Contact us at <a href="mailto:support@justburnt.com" className="text-purple-600 hover:text-purple-700">support@justburnt.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default WaitlistView;
